import {
  DateRangePicker,
  AvatarTrigger,
  PrimaryButton,
} from "@certn/cascadia-react";
import { _ResponsivePopover } from "@certn/cascadia-react/components/_assets/_ResponsivePopover";
import { css } from "@emotion/css";
import { CalendarDate, today, getLocalTimeZone } from "@internationalized/date";
import { useEffect, useState } from "react";
import { DateValue, DialogTrigger } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { useQueryParams } from "../../../hooks/useQueryParams";

interface RangeValue {
  start: DateValue;
  end: DateValue;
}

function parseIsoDate(value?: string): CalendarDate | null {
  if (!value) {
    return null;
  }
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new CalendarDate(year, month, day);
}

function formatDateRangeLabel(start: string, end: string) {
  const [startYear, startMonth, startDay] = start.split("-");
  const [endYear, endMonth, endDay] = end.split("-");
  return `${startMonth}/${startDay}/${startYear} - ${endMonth}/${endDay}/${endYear}`;
}

export function DateOrderedFilter() {
  const { t } = useTranslation();
  const { params, setParams } = useQueryParams();
  const label = t("Date ordered");
  const [isOpen, setOpen] = useState(false);
  const [filterLabel, setFilterLabel] = useState(label);

  const after = params.order_timeframe_after;
  const before = params.order_timeframe_before;

  useEffect(() => {
    if (!after) {
      setFilterLabel(label);
      return;
    }
    setFilterLabel(
      formatDateRangeLabel(
        after,
        before || today(getLocalTimeZone()).toString()
      )
    );
  }, [after, before, label]);

  const start = parseIsoDate(after);
  const end =
    parseIsoDate(before) || (start ? today(getLocalTimeZone()) : null);
  const rangeValue = start && end ? { start, end } : null;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setOpen}>
      <AvatarTrigger
        label={filterLabel}
        isOpen={isOpen}
        onToggle={() => setOpen((curr) => !curr)}
        count={after ? 1 : 0}
        selectedItems={[]}
        selectionMode="single"
        icon="calendar_month"
        testID="date-ordered-filter-trigger"
      />
      <_ResponsivePopover
        isDismissable
        isOpen={isOpen}
        onOpenChange={setOpen}
        popoverPlacement="bottom left"
        popoverMinWidth="280px"
        popoverMaxWidth="320px"
      >
        <DateRangePicker
          label={filterLabel}
          testID="date-ordered-filter"
          startName="order-timeframe-start"
          endName="order-timeframe-end"
          isStatic
          aria-label={t("Date ordered")}
          value={rangeValue}
          onChange={(value: RangeValue | null) => {
            const startDate = value?.start?.toString();
            const endDate = value?.end?.toString();
            if (startDate && endDate) {
              setParams((current) => {
                current.set("order_timeframe_after", startDate);
                current.set("order_timeframe_before", endDate);
                return current;
              });
              setOpen(false);
            }
          }}
        />
        <div
          className={css({
            display: "flex",
            justifyContent: "flex-end",
            padding: "var(--space-3) var(--space-4)",
          })}
        >
          <PrimaryButton
            testID="date-ordered-filter-clear"
            onPress={() => {
              setParams((current) => {
                current.delete("order_timeframe_after");
                current.delete("order_timeframe_before");
                return current;
              });
              setOpen(false);
            }}
          >
            {t("VERB Clear", "Clear")}
          </PrimaryButton>
        </div>
      </_ResponsivePopover>
    </DialogTrigger>
  );
}
